import React, { useEffect, useMemo, useState } from 'react';
import { Send, Loader2 } from 'lucide-react';
import { supabase } from '../../src/lib/supabase';
import { useAuth } from '../../src/contexts/AuthContext';
import type { Profile, Tarefa } from '../../types';
import { ComentarioTexto, corDoUsuario } from '../../src/components/comentarios/ComentarioTexto';
import type { TarefaMencionavel } from '../../src/components/comentarios/mencoes';
import MentionTextarea from '../Tarefas/MentionTextarea';
import type { ItemAtividade } from '../../src/lib/projetos/timeline';
import { carregarAtividadeProjeto } from '../../src/lib/projetos/carregarAtividade';

interface ProjetoAtividadeTimelineProps {
  projetoId: number;
  projetoNome: string;
  /** Tarefas vinculadas (mães e subtarefas) — fonte do "#" e dos comentários agregados. */
  tarefas: Tarefa[];
  users: Profile[];
}

const ProjetoAtividadeTimeline: React.FC<ProjetoAtividadeTimelineProps> = ({ projetoId, projetoNome, tarefas, users }) => {
  const { user, profile } = useAuth();
  const [itens, setItens] = useState<ItemAtividade[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [rascunho, setRascunho] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [mencoesUsuario, setMencoesUsuario] = useState<Set<string>>(new Set());
  const [mencoesTarefa, setMencoesTarefa] = useState<Set<string>>(new Set());

  const idsTarefa = useMemo(() => tarefas.map(t => t.id), [tarefas]);
  const chaveTarefas = idsTarefa.join(',');
  const titulos = useMemo(
    () => new Map(tarefas.map(t => [String(t.id), t.titulo])),
    [tarefas],
  );
  const mencionaveis: TarefaMencionavel[] = useMemo(
    () => tarefas.map(t => ({ id: t.id, titulo: t.titulo })),
    [tarefas],
  );

  const carregar = async () => {
    setCarregando(true);
    setItens(await carregarAtividadeProjeto(projetoId, idsTarefa, titulos));
    setCarregando(false);
  };

  useEffect(() => { carregar(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [projetoId, chaveTarefas]);

  const enviar = async () => {
    if (!rascunho.trim() || enviando || !user) return;
    setEnviando(true);
    setErro(null);
    const { error } = await supabase.from('comentarios_projetos')
      .insert([{ projeto_id: projetoId, user_id: user.id, conteudo: rascunho.trim() }]);

    if (error) {
      setErro(error.message);
      setEnviando(false);
      return;
    }

    // Menção notifica igual à de tarefa (views/Tarefas/TaskModal.tsx:274-285): o vínculo é o
    // Set capturado no clique, não um parse do texto.
    const notificacoes = [
      ...Array.from(mencoesUsuario).map(uid => ({
        user_id: uid,
        titulo: 'Você foi mencionado em um projeto',
        mensagem: `${profile?.full_name} mencionou você no projeto "${projetoNome}"`,
        link: `/projetos/${projetoId}`,
        lida: false,
      })),
      ...Array.from(mencoesTarefa)
        .map(tid => tarefas.find(t => String(t.id) === tid))
        .filter((t): t is Tarefa => !!t?.responsavel_id && t.responsavel_id !== user.id)
        .map(t => ({
          user_id: t.responsavel_id as string,
          titulo: 'Sua tarefa foi citada em um projeto',
          mensagem: `${profile?.full_name} citou "${t.titulo}" no projeto "${projetoNome}"`,
          link: `/projetos/${projetoId}`,
          lida: false,
        })),
    ];
    if (notificacoes.length > 0) await supabase.from('notificacoes').insert(notificacoes);

    setRascunho('');
    setMencoesUsuario(new Set());
    setMencoesTarefa(new Set());
    await carregar();
    setEnviando(false);
  };

  const autor = (uid: string | null) => users.find(u => u.id === uid);

  return (
    <div className="space-y-4">
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-4">
        <MentionTextarea
          users={users}
          tarefas={mencionaveis}
          value={rascunho}
          onChange={e => setRascunho(e.target.value)}
          onMention={uid => setMencoesUsuario(prev => new Set(prev).add(uid))}
          onTaskMention={tid => setMencoesTarefa(prev => new Set(prev).add(tid))}
          placeholder="Escreva uma atualização do projeto... suporta **markdown**, @ para pessoas e # para tarefas"
          className="px-3 py-2 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg text-sm dark:text-slate-200 resize-y min-h-[80px]"
        />
        {erro && <p className="text-xs text-rose-600 dark:text-rose-400 mt-1">{erro}</p>}
        <div className="flex justify-end mt-2">
          <button onClick={enviar} disabled={!rascunho.trim() || enviando}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white text-sm font-semibold rounded-lg disabled:opacity-50">
            {enviando ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            Publicar
          </button>
        </div>
      </div>

      {carregando ? (
        <div className="flex items-center gap-2 text-sm text-gray-500"><Loader2 size={14} className="animate-spin" /> Carregando atividade...</div>
      ) : itens.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400 py-8 text-center">Nada por aqui ainda.</p>
      ) : (
        <ul className="space-y-2">
          {itens.map(item => {
            const u = autor(item.userId);
            const paleta = corDoUsuario(item.userId || '');
            return (
              <li key={item.id}
                className={`bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 border-l-4 ${paleta.barra} rounded-lg p-3`}>
                <div className="flex flex-wrap items-center gap-2 text-xs mb-1">
                  <span className={`font-bold ${paleta.nome}`}>{u?.full_name || 'Alguém'}</span>
                  <span className="text-gray-400">
                    {new Date(item.createdAt).toLocaleString('pt-BR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                  </span>
                  <span className="px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-300">
                    {item.origem.tipo === 'projeto' ? 'Projeto' : item.origem.titulo}
                  </span>
                </div>
                {item.tipo === 'status' ? (
                  <p className="text-sm italic text-gray-500 dark:text-gray-400">{item.conteudo}</p>
                ) : (
                  <ComentarioTexto texto={item.conteudo} usuarios={users} tarefas={mencionaveis}
                    className="text-sm text-gray-700 dark:text-gray-200" />
                )}
                {item.anexos.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {item.anexos.map(url => (
                      <a key={url} href={url} target="_blank" rel="noopener noreferrer">
                        <img src={url} alt="anexo" className="h-20 rounded border border-gray-200 dark:border-gray-700" />
                      </a>
                    ))}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default ProjetoAtividadeTimeline;
